import type { CoralogixRum } from "@coralogix/browser";

type BeforeSend = NonNullable<
  Parameters<typeof CoralogixRum.init>[0]["beforeSend"]
>;
export type RumEvent = Parameters<BeforeSend>[0];

export type EventOrigin = "collins" | "evo" | "third-party" | "cross-origin";

export const EVENT_ORIGIN_LABEL = "origin";

/** Evo Workspace hosts: OpenComponents registry/content, Workspace APIs, cookie consent and Evo ESM dependencies. */
const EVO_DOMAINS = [
  "workspace.accessacloud.com",
  "ws.accessacloud.com",
  "preprodws.accessacloud.com",
  "demows.accessacloud.com",
  "go.accessacloud.com",
  "preprodgo.accessacloud.com",
  "sentiment.accessacloud.com",
  "access-evo.com",
  "esm.sh"
];

const THIRD_PARTY_DOMAINS = [
  "clarity.ms",
  "wootric.com",
  "wootric-eligibility.herokuapp.com",
  "intercom.io",
  "intercomcdn.com",
  "intercomassets.com",
  "googletagmanager.com",
  "google-analytics.com",
  "fonts.googleapis.com",
  "fonts.gstatic.com"
];

/** Browsers replace errors thrown by cross-origin scripts with this message and no stack. */
const CROSS_ORIGIN_ERROR_MESSAGE = "Script error.";

const matchesDomain = (hostname: string, domains: string[]): boolean =>
  domains.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
  );

const getHostname = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

const getEventUrl = (event: RumEvent): string | undefined =>
  event.network_request_context?.url ??
  event.resource_context?.name ??
  event.error_context?.original_stacktrace?.[0]?.fileName;

export const resolveEventOrigin = (event: RumEvent): EventOrigin => {
  if (event.error_context?.error_message === CROSS_ORIGIN_ERROR_MESSAGE) {
    return "cross-origin";
  }

  const hostname = getHostname(getEventUrl(event));
  if (!hostname) return "collins";
  if (matchesDomain(hostname, EVO_DOMAINS)) return "evo";
  if (matchesDomain(hostname, THIRD_PARTY_DOMAINS)) return "third-party";
  return "collins";
};

/**
 * Coralogix `beforeSend` hook that labels every RUM event with where it came
 * from, so Collins events can be filtered from the Evo and third-party
 * scripts running on the same page. Events are never dropped.
 */
export const labelEventOrigin = (event: RumEvent): RumEvent => ({
  ...event,
  labels: { ...event.labels, [EVENT_ORIGIN_LABEL]: resolveEventOrigin(event) }
});
